import React, { useId } from "react";
import { Input } from "./input";
import { Label } from "./label";
import { DualDateDisplay } from "./DualDateDisplay";
import { cn } from "./utils";

interface HijriDateInputProps {
  /** Current date value */
  value: Date | null;
  /** Callback when date changes */
  onChange: (date: Date) => void;
  /** Locale for display: 'en' for English, 'ar' for Arabic */
  locale?: "en" | "ar";
  /** Label text (English) */
  label?: string;
  /** Label text (Arabic) */
  labelAr?: string;
  /** Optional CSS class for the container */
  className?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Whether input is disabled */
  disabled?: boolean;
}

/**
 * Hijri Date Input Component
 *
 * Provides a native HTML date picker (Gregorian) with real-time Hijri
 * calendar preview below. Fully accessible with proper label association
 * and aria-describedby for the Hijri display.
 */
export const HijriDateInput = React.forwardRef<HTMLInputElement, HijriDateInputProps>(
  (
    {
      value,
      onChange,
      locale = "en",
      label,
      labelAr,
      className,
      placeholder = "YYYY-MM-DD",
      disabled = false,
    },
    ref
  ) => {
    const inputId = useId();
    const descriptionId = useId();
    const isAr = locale === "ar";

    // Convert Date to HTML date input format (YYYY-MM-DD)
    const inputValue = value
      ? value.toISOString().split("T")[0]
      : "";

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const dateString = e.target.value;
      if (dateString) {
        const d = new Date(dateString + "T00:00:00Z");
        if (!isNaN(d.getTime())) {
          onChange(d);
        }
      }
    };

    return (
      <div
        className={cn("space-y-2", className)}
        dir={isAr ? "rtl" : "ltr"}
      >
        {(label || labelAr) && (
          <Label htmlFor={inputId} className="text-xs">
            {isAr ? labelAr || label : label}
          </Label>
        )}
        <Input
          ref={ref}
          id={inputId}
          type="date"
          value={inputValue}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          aria-describedby={descriptionId}
          className={isAr ? "text-right" : ""}
        />
        {value && (
          <div
            id={descriptionId}
            className="text-xs text-slate-500 mt-1"
            role="status"
            aria-live="polite"
          >
            <span className="text-slate-400">
              {isAr ? "التاريخ الهجري: " : "Hijri: "}
            </span>
            <DualDateDisplay
              date={value}
              locale={locale}
              showHijri={true}
              className="inline"
            />
          </div>
        )}
      </div>
    );
  }
);

HijriDateInput.displayName = "HijriDateInput";
